import React from "react";
import is_null from "../../helpers/is_null";
import swal from "../../helpers/swal";
import { axiospost } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FiUploadCloud, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const UploadDoc = ({ r }) => {
  const [loading, setloading] = React.useState(false);
  const queryClient = useQueryClient();
  const isUploaded = r?.upload;

  const _save = async (e) => {
    try {
      setloading(true);
      const fd = new FormData();
      fd.append("photo", e.target.files[0]);
      fd.append("requirement", r._id);
      const req = await axiospost("upload_credential", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (req.status !== 200) throw new Error("Sorry something went wrong, please try again");
      const { type, message } = req.data;
      if (type === "error") throw new Error(message);
      queryClient.invalidateQueries({ queryKey: ["admissionRequirementforCourse"] });
      swal(type, message);
      setloading(false);
    } catch ({ message }) {
      swal("error", message);
      setloading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: loading ? 1 : 1.02 }}
      transition={{ duration: 0.2 }}
      className="relative"
    >
      <label
        htmlFor={r._id}
        className={`
          relative block p-6 rounded-lg border-2 transition-all duration-200
          ${loading ? 'cursor-wait' : 'cursor-pointer hover:shadow-md'}
          ${isUploaded
            ? 'border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100'
            : 'border-neutral-300 bg-white hover:border-primary-400 hover:bg-primary-50'
          }
        `}
      >
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          {isUploaded ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
              <FiCheckCircle className="h-3.5 w-3.5" />
              Uploaded
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
              <FiAlertCircle className="h-3.5 w-3.5" />
              Not Uploaded
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col items-center gap-3 mt-2">
          {loading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader />
              <p className="text-sm text-neutral-600 font-medium">Uploading...</p>
            </div>
          ) : (
            <>
              {/* Icon */}
              <div className={`
                h-16 w-16 rounded-full flex items-center justify-center transition-colors
                ${isUploaded
                  ? 'bg-green-100'
                  : 'bg-neutral-100'
                }
              `}>
                {isUploaded ? (
                  <FiCheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <FiUploadCloud className="h-8 w-8 text-neutral-600" />
                )}
              </div>

              {/* Label */}
              <div className="text-center">
                <p className={`
                  text-sm font-semibold mb-1
                  ${isUploaded ? 'text-green-900' : 'text-neutral-900'}
                `}>
                  {r?.label}
                </p>
                {r?.required && !isUploaded && (
                  <p className="text-xs text-orange-600 font-medium">
                    Required *
                  </p>
                )}
                {!loading && (
                  <p className={`
                    text-xs mt-2
                    ${isUploaded ? 'text-green-700' : 'text-primary-600 font-medium'}
                  `}>
                    {isUploaded ? 'Click to replace' : 'Click to upload'}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <input
          type="file"
          id={r._id}
          onChange={_save}
          disabled={loading}
          className="hidden"
          accept="image/*,.pdf"
        />
      </label>
    </motion.div>
  );
};

export default UploadDoc;
